// Keep in sync with chart-lib/src/utils/react-root.util.ts by hand until the
// shared @thoughtspot/react-compat-root package lands (tracked: SCAL-322015).
// Divergence: chart-lib uses renderIntoContainer/unmountContainer + an
// onRendered hook; this copy is renderReactRoot/unmountReactRoot with no hook.
// WeakMap contract: only renderReactRoot registers roots, so all renders must
// go through it — a stray render leaves a root unmountReactRoot can't tear down.
//
// React 18 by default (createRoot); legacy ReactDOM.render only when the server
// serves the React 17 fallback vendor set (see isReact18EnabledOnServer).

import ReactDOM, { flushSync } from 'react-dom';
// Namespace import, never named: externalized to window.libs.ReactDOMClientLib,
// which the React 17 fallback set does not define. A named import is rewritten
// to a destructure that throws at chunk load; the namespace just binds
// undefined. Assigned to a variable so rollup keeps the namespace object.
// eslint-disable-next-line no-restricted-imports -- this IS the react-root util.
import * as ReactDOMClient from 'react-dom/client';

const reactDomClientNamespace: unknown = ReactDOMClient;

interface CompatRoot {
    render(node: React.ReactElement): void;
    unmount(): void;
}

type CreateRootFn = (container: Element | DocumentFragment) => CompatRoot;

const rootsByContainer = new WeakMap<Element, CompatRoot>();

// React 18 is the default. nginx `$enableReact18 = false` (surfaced as
// `window.__enableReact18 === false` by the bootstrap inline script) serves the
// React 17 fallback vendor set, which has no createRoot, so this module must
// fall back to legacy ReactDOM.render. Only the server knows which react-dom was
// served, so only it can turn concurrent rendering off. Unset (dev, tests, no
// ssi) means React 18.
const SERVER_REACT18_FLAG_KEY = '__enableReact18';

function isReact18EnabledOnServer(): boolean {
    return (globalThis as any)[SERVER_REACT18_FLAG_KEY] !== false;
}

// Resolve createRoot lazily and cache it on globalThis so every compiled copy
// of this module (blink-v2 host + chart-lib) shares one resolution. Production
// reads the external module (window.libs.ReactDOMClientLib); dev/tests fall back
// to the bundled react-dom/client. Sticky cache: the vendor set loads once per
// session.
const RESOLVED_CREATE_ROOT_KEY = '__tsCompatResolvedCreateRoot';

const asFn = (f: unknown): CreateRootFn | undefined =>
    typeof f === 'function' ? (f as CreateRootFn) : undefined;

function getCreateRoot(): CreateRootFn | null {
    const g = globalThis as any;
    if (!g[RESOLVED_CREATE_ROOT_KEY]) {
        // CDN bundle exposes createRoot on the namespace; the local-assets
        // build wraps it under `default`. Check both delivery paths.
        const lib = g?.libs?.ReactDOMClientLib;
        const fallback: any = reactDomClientNamespace;
        const createRoot =
            asFn(lib?.createRoot) ??
            asFn(lib?.default?.createRoot) ??
            asFn(fallback?.createRoot) ??
            asFn(fallback?.default?.createRoot);
        if (createRoot) {
            g[RESOLVED_CREATE_ROOT_KEY] = createRoot;
        }
    }
    return (g[RESOLVED_CREATE_ROOT_KEY] as CreateRootFn) || null;
}

function createCompatRoot(container: Element): CompatRoot {
    if (isReact18EnabledOnServer()) {
        const createRoot = getCreateRoot();
        if (createRoot) {
            return createRoot(container);
        }
    }
    // React 17 fallback vendor set: plain legacy wrapper.
    return {
        render: node => {
            // eslint-disable-next-line react/no-deprecated
            ReactDOM.render(node, container);
        },
        unmount: () => {
            // eslint-disable-next-line react/no-deprecated
            ReactDOM.unmountComponentAtNode(container);
        },
    };
}

/**
 * The React 18 concurrent root schedules its initial commit asynchronously, so
 * flushSync forces it to commit before returning — every caller here is a
 * top-level mount that needs the tree (and its mount effects: session-loading
 * queries, cache writes) committed before it proceeds, else an unrelated
 * unmount/remount can race them. The legacy (React 17) render is already
 * synchronous and is left unwrapped.
 */
export function renderReactRoot(
    node: React.ReactElement,
    container: Element,
): void {
    let root = rootsByContainer.get(container);
    if (!root) {
        root = createCompatRoot(container);
        rootsByContainer.set(container, root);
    }
    const compatRoot = root;
    if (isReact18EnabledOnServer()) {
        flushSync(() => compatRoot.render(node));
    } else {
        compatRoot.render(node);
    }
}

export function unmountReactRoot(container: Element): void {
    const root = rootsByContainer.get(container);
    if (root) {
        root.unmount();
        rootsByContainer.delete(container);
    }
}
